/**
 * Blog structured data (TICKET-11098): BlogPosting JSON-LD for every post, plus FAQPage JSON-LD
 * for posts that have a "Frequently asked questions" section.
 */

export interface IFaqItem {
    question: string;
    answer: string;
}

/** Strips markdown/HTML down to the words, keeping link text. */
export function plainTextFromMarkdown(md: string): string {
    return md
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]+>/g, '')
        .replace(/^\s{0,3}(?:[-*+]|\d+\.)\s+/gm, '')
        .replace(/(\*\*|__|\*|_|`)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

const HEADING_RE = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const FAQ_HEADING_RE = /^frequently asked questions\??$/i;
// A bold line ending in "?", optionally followed by the answer on the same line.
const BOLD_QUESTION_RE = /^\s*(?:\*\*|__)(.+?\?)(?:\*\*|__)\s*(?:<br\s*\/?>)?\s*(.*)$/;

/**
 * Finds an H2/H3 "Frequently asked questions" heading and reads question/answer pairs under it until
 * the next H2, a horizontal rule, the author byline, or the end. Handles `**Question?**` or a `### Question?` sub-heading with the answer
 * below, and `**Question?**<br>Answer` on one line.
 */
export function extractFaqFromMarkdown(markdown: string | undefined): IFaqItem[] {
    if (!markdown) return [];
    const lines = markdown.split('\n');
    const start = lines.findIndex((line) => {
        const m = line.match(HEADING_RE);
        const level = m?.[1]?.length ?? 0;
        return level >= 2 && level <= 3 && FAQ_HEADING_RE.test(plainTextFromMarkdown(m?.[2] ?? ''));
    });
    if (start === -1) return [];

    const items: IFaqItem[] = [];
    let current: { question: string; answer: string[] } | undefined;
    const flush = () => {
        if (!current) return;
        const answer = plainTextFromMarkdown(current.answer.join(' '));
        if (answer) items.push({ question: current.question, answer });
        current = undefined;
    };

    let inCode = false;
    for (const line of lines.slice(start + 1)) {
        if (/^\s*```/.test(line)) inCode = !inCode;
        // The author byline (often after a horizontal rule) is not part of the last answer.
        if (!inCode && (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line) || /^written by\b/i.test(plainTextFromMarkdown(line)))) break;
        const heading = inCode ? null : line.match(HEADING_RE);
        if (heading) {
            if (heading[1]?.length === 2) break;
            flush();
            const text = plainTextFromMarkdown(heading[2] ?? '');
            if (text.endsWith('?')) current = { question: text, answer: [] };
            continue;
        }
        const q = inCode ? null : line.match(BOLD_QUESTION_RE);
        if (q) {
            flush();
            current = { question: plainTextFromMarkdown(q[1] ?? ''), answer: q[2] ? [q[2]] : [] };
        } else if (current && line.trim()) {
            current.answer.push(line);
        }
    }
    flush();
    return items;
}

/** A node of Nuxt Content's parsed `minimark` body: a text string or `[tag, props, ...children]`. */
export type TMinimarkNode = string | [string, Record<string, unknown>, ...TMinimarkNode[]];

function minimarkText(node: TMinimarkNode): string {
    if (typeof node === 'string') return node;
    const [tag, , ...children] = node;
    const text = children.map(minimarkText).join('');
    return ['li', 'p', 'br', 'tr'].includes(tag) ? `${text} ` : text;
}

/**
 * Rebuilds, from the parsed body the page already loads, the Markdown lines `extractFaqFromMarkdown` reads:
 * headings, bold-question paragraphs, rules and plain text. The raw Markdown is not stored by Nuxt Content.
 */
export function markdownFromMinimark(nodes: TMinimarkNode[] | undefined): string {
    return (nodes || [])
        .map((node) => {
            if (typeof node === 'string') return node;
            const [tag, , ...children] = node;
            if (/^h[1-6]$/.test(tag)) return `${'#'.repeat(Number(tag[1]))} ${minimarkText(node).trim()}`;
            if (tag === 'hr') return '---';
            if (tag === 'pre') return '';
            if (tag === 'p') {
                // Leading bold run, e.g. `**Question?**`, `**How do I move to** [**Unified.to**](…)**?**`, up to a <br>.
                const isBold = (n: TMinimarkNode) =>
                    typeof n === 'string'
                        ? !n.trim()
                        : n[0] === 'strong' || (n[0] === 'a' && n.slice(2).every((c) => typeof c !== 'string' && c[0] === 'strong'));
                const end = children.findIndex((n) => !isBold(n));
                const i = end === -1 ? children.length : end;
                const next = children[i];
                const bold = children.slice(0, i).map(minimarkText).join('').trim();
                if (bold.endsWith('?')) {
                    const rest = children
                        .slice(next && typeof next !== 'string' && next[0] === 'br' ? i + 1 : i)
                        .map(minimarkText)
                        .join('')
                        .trim();
                    return rest ? `**${bold}**<br>${rest}` : `**${bold}**`;
                }
            }
            return minimarkText(node).trim();
        })
        .join('\n\n');
}

export function buildFaqPageJsonLd(items: IFaqItem[]): Record<string, unknown> | undefined {
    if (!items.length) return undefined;
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
    };
}
